import { Component, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Clone, useGLTF } from '@react-three/drei'
import { Box3, Vector3, type Material, type Mesh } from 'three'
import { getModel, isModelBlob } from '../data/library'
import { useDebug, useDebugEnabled } from '../app/debug'
import { useBounds } from './bounds'
import { COKE_CAN_SIZE } from './constants'

// Bundle the DRACO decoder locally; drei otherwise defaults to a gstatic CDN (no-CDN/offline).
useGLTF.setDecoderPath(`${import.meta.env.BASE_URL}draco/`)

function useModelUrl(assetId: string, instanceId?: string): string | undefined {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    let active = true
    let created: string | undefined
    if (instanceId) useDebug.getState().report(instanceId, { status: 'loading', assetId })
    getModel(assetId).then(async (asset) => {
      if (!asset || !active) return
      if (!(await isModelBlob(asset.blob))) {
        if (instanceId) useDebug.getState().report(instanceId, { status: 'corrupt', assetId })
        return // corrupt — render nothing (listModels purges it)
      }
      created = URL.createObjectURL(asset.blob)
      setUrl(created)
    })
    return () => {
      active = false
      if (created) URL.revokeObjectURL(created)
    }
  }, [assetId, instanceId])
  return url
}

function DebugBox({ size, color }: { size: [number, number, number]; color: string }) {
  return (
    <mesh>
      <boxGeometry args={size} />
      <meshBasicMaterial color={color} wireframe transparent opacity={0.8} />
    </mesh>
  )
}

function Gltf({ url, assetId, instanceId }: { url: string; assetId: string; instanceId?: string }) {
  const { scene } = useGLTF(url)
  const debug = useDebugEnabled()

  useEffect(() => {
    // Transparent surfaces (e.g. the cabinet's glass doors) self-occlude with depthWrite on: the
    // first-drawn face writes depth and hides the panes behind it, so only one side reads as glass.
    // Disabling it lets every transparent surface blend consistently.
    scene.traverse((o) => {
      const mat = (o as Mesh).material
      for (const m of Array.isArray(mat) ? mat : mat ? [mat] : []) {
        if ((m as Material).transparent) (m as Material).depthWrite = false
      }
    })
  }, [scene])

  const { fit, center, maxDim, normalized } = useMemo(() => {
    const box = new Box3().setFromObject(scene, true) // precise: exact world-space vertex bounds
    if (box.isEmpty())
      return {
        fit: 1,
        center: new Vector3(),
        maxDim: 0,
        normalized: [0, 0, 0] as [number, number, number],
      }
    const size = box.getSize(new Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    const fit = maxDim > 0 && Number.isFinite(maxDim) ? COKE_CAN_SIZE / maxDim : 1
    const normalized: [number, number, number] = [size.x * fit, size.y * fit, size.z * fit]
    return { fit, center: box.getCenter(new Vector3()), maxDim, normalized }
  }, [scene])

  useEffect(() => {
    // Publish the normalized box so Grabbable can size its collider to the actual model.
    useBounds.getState().set(assetId, normalized)
    // assetId was already recorded by useModelUrl's 'loading' report; report() merges.
    if (instanceId)
      useDebug.getState().report(instanceId, { status: 'ready', size: normalized, maxDim, fit })
  }, [assetId, instanceId, normalized, fit, maxDim])

  return (
    <>
      <group scale={fit}>
        <group position={[-center.x, -center.y, -center.z]}>
          <Clone object={scene} />
        </group>
      </group>
      {debug && <DebugBox size={normalized} color="#00ff88" />}
    </>
  )
}

// Surface on-device load/parse failures (useGLTF rejects → would otherwise render blank). Reports
// the error to the debug store and, in debug mode, drops a red marker box at the spawn point.
class ModelErrorBoundary extends Component<
  { instanceId?: string; debug: boolean; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error: unknown) {
    if (this.props.instanceId)
      useDebug.getState().report(this.props.instanceId, { status: 'error', error: String(error) })
  }
  render() {
    if (this.state.failed)
      return this.props.debug ? <DebugBox size={[0.12, 0.12, 0.12]} color="#ff3344" /> : null
    return this.props.children
  }
}

export function Model({ assetId, instanceId }: { assetId: string; instanceId?: string }) {
  const url = useModelUrl(assetId, instanceId)
  const debug = useDebugEnabled()
  useEffect(
    () => () => (instanceId ? useDebug.getState().drop(instanceId) : undefined),
    [instanceId],
  )
  if (url == null) return null
  return (
    <ModelErrorBoundary instanceId={instanceId} debug={debug}>
      <Suspense fallback={debug ? <DebugBox size={[0.12, 0.12, 0.12]} color="#ffcc00" /> : null}>
        <Gltf url={url} assetId={assetId} instanceId={instanceId} />
      </Suspense>
    </ModelErrorBoundary>
  )
}
